import {
    Divider,
    Grid,
    Paper,
    Typography,
} from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { TemperatureField } from "components/LLMs/TemperatureField";
import type {
    KboAvailableModel,
    KboAnalysisSettings,
    KboAnalysisPromptsSettings,
    KboSettings,
} from "components/KBo/Models/KboSettings";
import type { LLMGenerationSettings } from "models/LLMs";
import React, { useCallback } from "react";
import {
    renderSettingsFieldLabel,
    SettingsFieldLabel,
} from "./SettingsFieldLabel";
import { AvailableTags } from "./AvailableTags";
import { KBoModelGenerationSettings } from "./KBoModelGenerationSettings";

interface Props {
    analysis?: KboAnalysisSettings;
    analysisPrompts?: KboAnalysisPromptsSettings;
    availableModels?: KboAvailableModel[];
    onChange: (patch: {
        analysis?: KboAnalysisSettings;
        analysisPrompts?: KboAnalysisPromptsSettings;
    }) => void;
    defaults?: KboSettings | null;
    templateTags?: Record<string, string[]>;
}

export function KBoAnalysisConfiguration({
    analysis,
    analysisPrompts,
    availableModels,
    onChange,
    defaults,
    templateTags,
}: Props) {
    const handleAnalysisChange = useCallback(
        (field: keyof KboAnalysisSettings, value: string) => {
            const num = parseInt(value, 10);
            onChange({
                analysis: {
                    ...analysis,
                    [field]: isNaN(num) ? null : Math.max(num, 1),
                },
            });
        },
        [analysis, onChange],
    );

    const handleAnalysisReset = useCallback(
        (field: keyof KboAnalysisSettings) => {
            onChange({
                analysis: { ...analysis, [field]: null },
            });
        },
        [analysis, onChange],
    );

    const handlePromptChange = useCallback(
        (
            field: keyof KboAnalysisPromptsSettings,
            value: string | number | null,
        ) => {
            onChange({
                analysisPrompts: { ...analysisPrompts, [field]: value },
            });
        },
        [analysisPrompts, onChange],
    );

    const handleTemperature = useCallback(
        (field: keyof KboAnalysisPromptsSettings, value: number | null) => {
            onChange({
                analysisPrompts: {
                    ...analysisPrompts,
                    [field]: value,
                },
            });
        },
        [analysisPrompts, onChange],
    );

    const conversationValue = analysis?.conversationAnalysis ?? null;
    const conversationDefault =
        defaults?.analysis?.conversationAnalysis ?? null;
    const conversationSummaryValue =
        analysis?.conversationSummaryAnalysis ?? null;
    const conversationSummaryDefault =
        defaults?.analysis?.conversationSummaryAnalysis ?? null;
    const modelResultValue = analysis?.modelResultAnalysis ?? null;
    const modelResultDefault =
        defaults?.analysis?.modelResultAnalysis ?? null;
    const updateConversationGeneration = useCallback(
        (value: LLMGenerationSettings | null) => {
            const temperature = value?.temperature ?? null;
            onChange({
                analysis: {
                    ...analysis,
                    conversationAnalysis: value,
                },
                analysisPrompts: {
                    ...analysisPrompts,
                    singleTranscriptAnalysisTemperature: temperature,
                    multiTranscriptAnalysisTemperature: temperature,
                    additionalInsightsAnalysisTemperature: temperature,
                },
            });
        },
        [analysis, analysisPrompts, onChange],
    );

    const updateConversationSummaryGeneration = useCallback(
        (value: LLMGenerationSettings | null) => {
            const temperature = value?.temperature ?? null;
            onChange({
                analysis: {
                    ...analysis,
                    conversationSummaryAnalysis: value,
                },
                analysisPrompts: {
                    ...analysisPrompts,
                    conversationSummaryAnalysisTemperature: temperature,
                    additionalConversationSummaryAnalysisTemperature:
                        temperature,
                },
            });
        },
        [analysis, analysisPrompts, onChange],
    );

    const updateModelResultGeneration = useCallback(
        (value: LLMGenerationSettings | null) => {
            const temperature = value?.temperature ?? null;
            onChange({
                analysis: {
                    ...analysis,
                    modelResultAnalysis: value,
                },
                analysisPrompts: {
                    ...analysisPrompts,
                    deeperAnalysisTemperature: temperature,
                    singleResultDeeperAnalysisTemperature: temperature,
                    summaryTemperature: temperature,
                    singleResultSummaryTemperature: temperature,
                },
            });
        },
        [analysis, analysisPrompts, onChange],
    );

    return (
        <Grid container direction="column" rowGap={3}>
            {/* Analyze Conversations */}
            <Grid item>
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                >
                    Analyze Conversations
                </Typography>
                <Divider />
            </Grid>

            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid
                            container
                            item
                            spacing={2}
                            flexWrap="wrap"
                        >
                            <Grid item xs={6}>
                                <KBoModelGenerationSettings
                                    id="kbo-conv-analysis"
                                    availableModels={availableModels ?? []}
                                    value={conversationValue}
                                    defaultValue={conversationDefault}
                                    modelLabel="Conversation Analysis Model"
                                    temperatureLabel="Conversation Analysis Temperature"
                                    modelPlaceholder="Use main agent model..."
                                    renderLabel={renderSettingsFieldLabel}
                                    onChange={updateConversationGeneration}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <KBoModelGenerationSettings
                                    id="kbo-conv-summary-analysis"
                                    availableModels={availableModels ?? []}
                                    value={conversationSummaryValue}
                                    defaultValue={conversationSummaryDefault}
                                    modelLabel="Conversation Summary Analysis Model"
                                    temperatureLabel="Conversation Summary Analysis Temperature"
                                    modelPlaceholder="Use gpt-5.4..."
                                    renderLabel={renderSettingsFieldLabel}
                                    onChange={
                                        updateConversationSummaryGeneration
                                    }
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Transcript Sample Size"
                                    isDefault={
                                        analysis?.insightsTranscriptSampleSize ==
                                        null
                                    }
                                    onReset={() =>
                                        handleAnalysisReset(
                                            "insightsTranscriptSampleSize",
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-insights-sample-size"
                                    type="number"
                                    value={
                                        analysis?.insightsTranscriptSampleSize ??
                                        defaults?.analysis
                                            ?.insightsTranscriptSampleSize ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        handleAnalysisChange(
                                            "insightsTranscriptSampleSize",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 500 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Summary Sample Size"
                                    isDefault={
                                        analysis?.insightsSummarySampleSize ==
                                        null
                                    }
                                    onReset={() =>
                                        handleAnalysisReset(
                                            "insightsSummarySampleSize",
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-insights-summary-sample-size"
                                    type="number"
                                    value={
                                        analysis?.insightsSummarySampleSize ??
                                        defaults?.analysis
                                            ?.insightsSummarySampleSize ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        handleAnalysisChange(
                                            "insightsSummarySampleSize",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 2000 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Transcript Max"
                                    isDefault={
                                        analysis?.insightsMaxConversationCount ==
                                        null
                                    }
                                    onReset={() =>
                                        handleAnalysisReset(
                                            "insightsMaxConversationCount",
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-insights-max-conversations"
                                    type="number"
                                    value={
                                        analysis?.insightsMaxConversationCount ??
                                        defaults?.analysis
                                            ?.insightsMaxConversationCount ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        handleAnalysisChange(
                                            "insightsMaxConversationCount",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 5000 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Summary Max"
                                    isDefault={
                                        analysis?.insightsSummaryMaxConversationCount ==
                                        null
                                    }
                                    onReset={() =>
                                        handleAnalysisReset(
                                            "insightsSummaryMaxConversationCount",
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-insights-summary-max-conversations"
                                    type="number"
                                    value={
                                        analysis?.insightsSummaryMaxConversationCount ??
                                        defaults?.analysis
                                            ?.insightsSummaryMaxConversationCount ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        handleAnalysisChange(
                                            "insightsSummaryMaxConversationCount",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 5000 }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <AnalysisPromptSection
                title="Single Transcript Analysis"
                description="Prompt for analyzing a single call transcript. Uses {0} positional placeholder for the transcript."
                promptId="kbo-single-transcript"
                promptField="singleTranscriptAnalysis"
                tempField="singleTranscriptAnalysisTemperature"
                promptValue={analysisPrompts?.singleTranscriptAnalysis}
                tempValue={
                    analysisPrompts?.singleTranscriptAnalysisTemperature
                }
                defaultPrompt={
                    defaults?.analysisPrompts?.singleTranscriptAnalysis
                }
                defaultTemp={
                    defaults?.analysisPrompts
                        ?.singleTranscriptAnalysisTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                showTemperature={false}
            />

            <AnalysisPromptSection
                title="Multi-Transcript Analysis"
                description="Prompt for analyzing multiple transcripts together."
                promptId="kbo-multi-transcript"
                promptField="multiTranscriptAnalysis"
                tempField="multiTranscriptAnalysisTemperature"
                promptValue={analysisPrompts?.multiTranscriptAnalysis}
                tempValue={
                    analysisPrompts?.multiTranscriptAnalysisTemperature
                }
                defaultPrompt={
                    defaults?.analysisPrompts?.multiTranscriptAnalysis
                }
                defaultTemp={
                    defaults?.analysisPrompts
                        ?.multiTranscriptAnalysisTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.multiTranscriptAnalysis}
                showTemperature={false}
            />

            <AnalysisPromptSection
                title="Additional Insights Analysis"
                description="Prompt for targeted analysis around a specific topic."
                promptId="kbo-additional-insights"
                promptField="additionalInsightsAnalysis"
                tempField="additionalInsightsAnalysisTemperature"
                promptValue={analysisPrompts?.additionalInsightsAnalysis}
                tempValue={
                    analysisPrompts?.additionalInsightsAnalysisTemperature
                }
                defaultPrompt={
                    defaults?.analysisPrompts?.additionalInsightsAnalysis
                }
                defaultTemp={
                    defaults?.analysisPrompts
                        ?.additionalInsightsAnalysisTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.additionalInsightsAnalysis}
                showTemperature={false}
            />

            <AnalysisPromptSection
                title="Conversation Summary Analysis"
                description="Prompt for analyzing generated conversation summaries together."
                promptId="kbo-conversation-summary-analysis"
                promptField="conversationSummaryAnalysis"
                tempField="conversationSummaryAnalysisTemperature"
                promptValue={analysisPrompts?.conversationSummaryAnalysis}
                tempValue={
                    analysisPrompts?.conversationSummaryAnalysisTemperature
                }
                defaultPrompt={
                    defaults?.analysisPrompts
                        ?.conversationSummaryAnalysis
                }
                defaultTemp={
                    defaults?.analysisPrompts
                        ?.conversationSummaryAnalysisTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.conversationSummaryAnalysis}
                showTemperature={false}
            />

            <AnalysisPromptSection
                title="Additional Conversation Summary Analysis"
                description="Prompt for targeted analysis around a specific topic using generated conversation summaries."
                promptId="kbo-additional-conversation-summary-analysis"
                promptField="additionalConversationSummaryAnalysis"
                tempField="additionalConversationSummaryAnalysisTemperature"
                promptValue={
                    analysisPrompts?.additionalConversationSummaryAnalysis
                }
                tempValue={
                    analysisPrompts?.additionalConversationSummaryAnalysisTemperature
                }
                defaultPrompt={
                    defaults?.analysisPrompts
                        ?.additionalConversationSummaryAnalysis
                }
                defaultTemp={
                    defaults?.analysisPrompts
                        ?.additionalConversationSummaryAnalysisTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.additionalConversationSummaryAnalysis}
                showTemperature={false}
            />

            {/* Analyze Model Results */}
            <Grid item sx={{ mt: 2 }}>
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                >
                    Analyze Model Results
                </Typography>
                <Divider />
            </Grid>

            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                        >
                            <Grid item xs={4}>
                                <KBoModelGenerationSettings
                                    id="kbo-mr-analysis"
                                    availableModels={availableModels ?? []}
                                    value={modelResultValue}
                                    defaultValue={modelResultDefault}
                                    modelLabel="Model Result Analysis Model"
                                    temperatureLabel="Model Result Analysis Temperature"
                                    modelPlaceholder="Use main agent model..."
                                    renderLabel={renderSettingsFieldLabel}
                                    onChange={updateModelResultGeneration}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Conversations Per Result"
                                    isDefault={
                                        analysis?.modelResultConversationsPerResult ==
                                        null
                                    }
                                    onReset={() =>
                                        handleAnalysisReset(
                                            "modelResultConversationsPerResult",
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-mr-conversations-per-result"
                                    type="number"
                                    value={
                                        analysis?.modelResultConversationsPerResult ??
                                        defaults?.analysis
                                            ?.modelResultConversationsPerResult ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        handleAnalysisChange(
                                            "modelResultConversationsPerResult",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 100 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Single Result Conversations"
                                    isDefault={
                                        analysis?.modelResultConversationsForSingleResult ==
                                        null
                                    }
                                    onReset={() =>
                                        handleAnalysisReset(
                                            "modelResultConversationsForSingleResult",
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-mr-conversations-single"
                                    type="number"
                                    value={
                                        analysis?.modelResultConversationsForSingleResult ??
                                        defaults?.analysis
                                            ?.modelResultConversationsForSingleResult ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        handleAnalysisChange(
                                            "modelResultConversationsForSingleResult",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 100 }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <AnalysisPromptSection
                title="Deeper Analysis"
                description="Prompt for generating insights from model result reasoning across multiple results."
                promptId="kbo-deeper-analysis"
                promptField="deeperAnalysisPrompt"
                tempField="deeperAnalysisTemperature"
                promptValue={analysisPrompts?.deeperAnalysisPrompt}
                tempValue={analysisPrompts?.deeperAnalysisTemperature}
                defaultPrompt={
                    defaults?.analysisPrompts?.deeperAnalysisPrompt
                }
                defaultTemp={
                    defaults?.analysisPrompts?.deeperAnalysisTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.deeperAnalysisPrompt}
                showTemperature={false}
            />

            <AnalysisPromptSection
                title="Single Result Deeper Analysis"
                description="Prompt for generating insights from a single model result."
                promptId="kbo-single-result-deeper"
                promptField="singleResultDeeperAnalysis"
                tempField="singleResultDeeperAnalysisTemperature"
                promptValue={analysisPrompts?.singleResultDeeperAnalysis}
                tempValue={
                    analysisPrompts?.singleResultDeeperAnalysisTemperature
                }
                defaultPrompt={
                    defaults?.analysisPrompts?.singleResultDeeperAnalysis
                }
                defaultTemp={
                    defaults?.analysisPrompts
                        ?.singleResultDeeperAnalysisTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.singleResultDeeperAnalysis}
                showTemperature={false}
            />

            <AnalysisPromptSection
                title="Summary"
                description="Prompt for summarizing model result reasoning patterns."
                promptId="kbo-summary"
                promptField="summaryPrompt"
                tempField="summaryTemperature"
                promptValue={analysisPrompts?.summaryPrompt}
                tempValue={analysisPrompts?.summaryTemperature}
                defaultPrompt={defaults?.analysisPrompts?.summaryPrompt}
                defaultTemp={
                    defaults?.analysisPrompts?.summaryTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.summaryPrompt}
                showTemperature={false}
            />

            <AnalysisPromptSection
                title="Single Result Summary"
                description="Prompt for summarizing reasoning for a single model result."
                promptId="kbo-single-result-summary"
                promptField="singleResultSummaryPrompt"
                tempField="singleResultSummaryTemperature"
                promptValue={analysisPrompts?.singleResultSummaryPrompt}
                tempValue={
                    analysisPrompts?.singleResultSummaryTemperature
                }
                defaultPrompt={
                    defaults?.analysisPrompts?.singleResultSummaryPrompt
                }
                defaultTemp={
                    defaults?.analysisPrompts
                        ?.singleResultSummaryTemperature
                }
                onPromptChange={handlePromptChange}
                onTempChange={handleTemperature}
                tags={templateTags?.singleResultSummaryPrompt}
                showTemperature={false}
            />
        </Grid>
    );
}

interface AnalysisPromptSectionProps {
    title: string;
    description: string;
    promptId: string;
    promptField: keyof KboAnalysisPromptsSettings;
    tempField: keyof KboAnalysisPromptsSettings;
    promptValue?: string | null;
    tempValue?: number | null;
    defaultPrompt?: string | null;
    defaultTemp?: number | null;
    onPromptChange: (
        field: keyof KboAnalysisPromptsSettings,
        value: string | null,
    ) => void;
    onTempChange: (
        field: keyof KboAnalysisPromptsSettings,
        value: number | null,
    ) => void;
    tags?: string[];
    showTemperature?: boolean;
}

function AnalysisPromptSection({
    title,
    description,
    promptId,
    promptField,
    tempField,
    promptValue,
    tempValue,
    defaultPrompt,
    defaultTemp,
    onPromptChange,
    onTempChange,
    tags,
    showTemperature = true,
}: AnalysisPromptSectionProps) {
    return (
        <Grid item>
            <Paper>
                <Grid container direction="column" rowGap={2} padding={3}>
                    <Grid item>
                        <Typography variant="h6">{title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        item
                        columnGap={2}
                        flexWrap="nowrap"
                        alignItems="flex-start"
                    >
                        <Grid item xs>
                            <SettingsFieldLabel
                                label="Prompt"
                                isDefault={promptValue == null}
                                onReset={() =>
                                    onPromptChange(promptField, null)
                                }
                            />
                            <AcxMainTextField
                                id={`${promptId}-prompt`}
                                fullWidth
                                multiline
                                rowsMin={4}
                                rowsMax={16}
                                value={
                                    promptValue ?? defaultPrompt ?? ""
                                }
                                onChange={(e) =>
                                    onPromptChange(
                                        promptField,
                                        e.target.value || null,
                                    )
                                }
                            />
                            <AvailableTags tags={tags} />
                        </Grid>
                        {showTemperature && (
                            <Grid item xs={2}>
                                <SettingsFieldLabel
                                    label="Temperature"
                                    isDefault={tempValue == null}
                                    onReset={() =>
                                        onPromptChange(tempField, null)
                                    }
                                />
                                <TemperatureField
                                    id={`${promptId}-temp`}
                                    value={tempValue ?? null}
                                    placeholder={defaultTemp ?? null}
                                    onChange={(value) =>
                                        onTempChange(tempField, value)
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
}
